import { X } from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/utils';

/**
 * A list of values you pick from a known set, or type yourself when the field
 * allows it.
 *
 * Both composers used to take repos as one comma-separated string, so a typo
 * minted a phantom project: `nymbl-ap` became a repo in the sidebar, with a
 * brief filed against it that no device is authorised for. The known repos
 * were the fix for the enrollment composer's own repo field, which still
 * needs to accept a genuinely new one - a device can be mapped to a repo
 * before anyone has registered it.
 *
 * The job composer's repos field (issue #101) is the other caller, and for it
 * `allowCustom` is false: the registry that closed the typo class of bug for
 * good is exactly the list `options` carries there, so a value has to come
 * from it. Typing something the list does not contain still fills the field -
 * a person can see what they typed and why it did not commit - it simply
 * never becomes a chip.
 *
 * Built rather than pulled in: the app has no popover, command or combobox
 * primitive, and this is the whole behaviour the two forms need. It is the same
 * shape `Menu` uses - anchored by an absolutely positioned child rather than a
 * portal, closed by Escape or a pointer outside, driven by the arrows - with the
 * one addition that the trigger is a text field and the value is a set.
 */
export function Combobox({
  id,
  values,
  onChange,
  options,
  placeholder,
  required = false,
  allowCustom = true,
  className,
}: {
  /** The id the form's own `<Label htmlFor>` points at: this goes on the input. */
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  /** What the field suggests, and (when `allowCustom` is false) the only values it will accept. */
  options: readonly string[];
  placeholder?: string;
  /**
   * Whether the field must end up with at least one value. It is set on the
   * input only while nothing has been picked, so the browser's own validation
   * still speaks for the form and a field with chips in it is never "empty".
   */
  required?: boolean;
  /** False for a field gated to a registry: a typed value that is not in `options` never becomes a chip. */
  allowCustom?: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  // -1 is "nothing highlighted", and it is the resting state on purpose: Enter
  // then commits what was typed, which is what a person typing a repo the list
  // has never seen means by it. A suggestion only takes Enter once the arrows
  // (or the pointer) have actually landed on one.
  const [active, setActive] = useState(-1);
  const wrap = useRef<HTMLDivElement>(null);
  const listId = useId();

  const query = draft.trim().toLowerCase();
  const suggestions = options.filter(
    (option) => !values.includes(option) && (!query || option.toLowerCase().includes(query)),
  );
  // "Open" is a state of this component; "expanded" is a claim about a listbox
  // being on screen, and there is none when nothing matches what was typed.
  const expanded = open && suggestions.length > 0;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  const add = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setDraft('');
      setActive(-1);
      return;
    }
    // Gated: leave an unmatched draft sitting in the field rather than
    // discarding it silently. A person who typed a repo the registry does
    // not have should see that nothing happened, not wonder where it went.
    if (!allowCustom && !options.includes(trimmed)) return;
    if (!values.includes(trimmed)) onChange([...values, trimmed]);
    setDraft('');
    setActive(-1);
  };

  const remove = (value: string) => {
    // Putting a repo back changes which rows the list has, so the highlight goes
    // back to nothing rather than pointing at whatever moved into its place.
    setActive(-1);
    onChange(values.filter((existing) => existing !== value));
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (!suggestions.length) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActive((previous) => {
        // From nothing, down lands on the first row and up on the last.
        if (previous < 0) return delta > 0 ? 0 : suggestions.length - 1;
        return (previous + delta + suggestions.length) % suggestions.length;
      });
      return;
    }
    // A comma is always the typed text: it is how a person separates repos, and
    // it must not hand them a suggestion they were only scrolling past.
    if (event.key === ',') {
      if (!draft.trim()) return;
      event.preventDefault();
      add(draft);
      return;
    }
    // Enter takes the highlighted suggestion when there is one, and what was
    // typed when there is not. Neither submits the form from here: a half-typed
    // repo must not become a filed brief.
    if (event.key === 'Enter') {
      const picked = expanded && active >= 0 ? suggestions[active] : undefined;
      if (!picked && !draft.trim()) return;
      event.preventDefault();
      add(picked ?? draft);
      setOpen(false);
      return;
    }
    if (event.key === 'Escape' && open) {
      // Only the list closes; the form's own Escape handler still gets the key
      // when there is no list to close.
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === 'Backspace' && !draft && values.length) {
      event.preventDefault();
      remove(values[values.length - 1]!);
    }
  };

  return (
    <div ref={wrap} className={cn('relative', className)}>
      {/* The field itself fills the row, so the box is a border rather than a
          control: everything interactive in here is the input or a chip's own
          button, and the keyboard never has to reach the box. */}
      <div
        className={cn(
          'border-line-2 flex min-h-7 w-full flex-wrap items-center gap-1 rounded-md border px-1.5 py-1',
          'transition-colors duration-100 focus-within:border-accent-line',
        )}
      >
        {values.map((value) => (
          <span
            key={value}
            className="bg-raised text-fg-2 inline-flex h-[18px] shrink-0 items-center gap-1 rounded-sm px-1.5 text-2xs"
          >
            {value}
            <button
              type="button"
              onClick={() => remove(value)}
              aria-label={`Remove ${value}`}
              // The icon is 10px; the target around it is not. Padding pulled back
              // out with a negative margin, so the chip keeps its 18px height.
              className="hover:text-foreground -m-0.5 p-1"
            >
              <X className="size-2.5" aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={id}
          role="combobox"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-activedescendant={
            expanded && suggestions[active] ? `${listId}-${active}` : undefined
          }
          aria-autocomplete="list"
          value={draft}
          required={required && values.length === 0}
          placeholder={values.length ? '' : placeholder}
          onChange={(event) => {
            setDraft(event.target.value);
            setActive(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          // Whatever is half-typed when focus leaves is meant: committing it here
          // is what stops a repo being lost between the field and the submit.
          onBlur={() => {
            if (draft.trim()) add(draft);
            setOpen(false);
          }}
          onKeyDown={onKeyDown}
          className="placeholder:text-fg-3 h-[18px] min-w-[7rem] flex-1 bg-transparent text-sm outline-none"
        />
      </div>

      {expanded ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Known repos"
          className={cn(
            'bg-pop dy-scroll dy-pop-in absolute top-[calc(100%+4px)] z-40 max-h-[14rem] w-full overflow-y-auto',
            'rounded-lg border p-1 shadow-pop',
          )}
        >
          {suggestions.map((option, index) => (
            <button
              key={option}
              type="button"
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              tabIndex={-1}
              // Keeps focus in the field, so the blur that would otherwise fire
              // before the click never happens and the pick lands.
              onMouseDown={(event) => event.preventDefault()}
              onMouseMove={() => setActive(index)}
              onClick={() => add(option)}
              className={cn(
                'flex w-full items-center rounded-sm px-2 py-1 text-left text-sm max-sm:py-2',
                index === active ? 'bg-raised text-foreground' : 'text-fg-2',
              )}
            >
              <span className="min-w-0 truncate">{option}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
